import BodyText from 'LuxKit/Typography/BodyText'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import { useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import type { CABIN_CATEGORY_TYPE } from 'constants/cruise'
import type { CABIN_CATEGORY_LABEL_TYPE_BOTH, CABIN_CATEGORY_LABEL_TYPE_RIVER, CabinTypeMap } from '../CruiseSelectCabinType'
import useCruisePriceByNight from 'hooks/useCruisePriceByNight'
import CruiseCabinTypeGrid from 'components/Cruises/Common/CruiseCabinTypeGrid'
import CruiseCabinTypeTileWrapper from 'components/Cruises/Common/CruiseCabinTypeTileWrapper'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'
import useCruiseCabinDescriptions from 'hooks/Cruise/useCruiseCabinDescriptions'

interface Props {
  cabinTypeMap: CabinTypeMap

  // Cabin type labels could differ depending on the cruise types in the search results.
  cabinTypeLabels: Array<CABIN_CATEGORY_TYPE | CABIN_CATEGORY_LABEL_TYPE_RIVER | CABIN_CATEGORY_LABEL_TYPE_BOTH>
}

function CruiseCabinTypeInfoModal({ cabinTypeMap, cabinTypeLabels }: Props) {
  const intl = useIntl()
  const modalState = useModalElementContext<string>()
  const isPricePerNight = useCruisePriceByNight()
  const cabinTypeLocalLabels = useCruiseCabinTypeLabels()
  const cabinDescriptions = useCruiseCabinDescriptions()
  const priceProperties = isPricePerNight ? 'minimumPricePerNight' : 'minimumPrice'

  const fromCaption = intl.formatMessage({
    id: 'zAxf2S0Xj6cAu0Z6wJ1e',
    defaultMessage: 'From',
    description: 'Price caption indicating starting price for cabin type',
  })

  const perNightUnit = intl.formatMessage({
    id: 'G0BS0iyjPnvV9WT3ltXh',
    defaultMessage: '/night',
    description: 'Per-night price unit suffix displayed after cabin type price in cruise info modal',
  })

  const perPersonUnit = intl.formatMessage({
    id: 'MkkQ1KbsIjyWP2Ozcd5R',
    defaultMessage: '/person',
    description: 'Price unit suffix for per-person cabin type pricing',
  })

  return <Modal
    size="L"
    title={intl.formatMessage({
      id: 'Or0IK5_jKCotm3F3BwnO',
      defaultMessage: 'Which cabin type is right for me?',
      description: 'Modal title for cabin type selection in the cruise cabin selection area',
    })}
    subtitle={intl.formatMessage({
      id: 'vdnaCxoiScbMp7zBvwwt',
      defaultMessage: 'Find the perfect cruise cabin to match your needs.',
      description: 'Modal subtitle for cabin type selection in the cruise offer or booking section',
    })}
  >
    <Group gap={24} direction="vertical">
      <CruiseCabinTypeGrid>
        {cabinTypeLabels.map((cabinType) => (
          <CruiseCabinTypeTileWrapper
            key={cabinType}
            direction="vertical"
            gap={12}
          >
            <VerticalSpacer gap={16}>
              <Heading variant="heading4">{cabinTypeLocalLabels[cabinType] ?? cabinType}</Heading>
              <BodyText variant="small">
                {cabinDescriptions[cabinType]}
              </BodyText>
            </VerticalSpacer>
            <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
              <Group direction="vertical" verticalAlign="center" gap={2}>
                <Group direction="horizontal" verticalAlign="center" gap={2}>
                  <PriceRowPriceWithCaption
                    price={cabinTypeMap[cabinType]?.[priceProperties]}
                    size="S"
                    caption={fromCaption}
                    saleUnit={isPricePerNight ? perNightUnit : perPersonUnit}
                  />
                </Group>
              </Group>

              <TextButton
                kind="secondary"
                onClick={() => modalState.resolve(cabinType)}
              >
                {intl.formatMessage({
                  id: 'dAP58pcbwsINC8PBacLy',
                  defaultMessage: 'View',
                  description: 'Button label to view cabin type details in the cruise cabin selection area',
                })}
              </TextButton>
            </Group>
          </CruiseCabinTypeTileWrapper>
        ))}
      </CruiseCabinTypeGrid>
    </Group>
  </Modal>
}

export default CruiseCabinTypeInfoModal
